import React, { useState, useEffect, useRef } from 'react';
import { usePayrollHoursText } from '../../hooks/usePayrollHoursText';
import { Clock, Calendar, User, CheckCircle, XCircle, FileText, AlertCircle, Package, DollarSign, ChevronDown } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { getPayrollRequestsForBusiness, updatePayrollRequestStatus, approvePayrollRequest } from '../../lib/payrollRequests';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';

interface PayrollRequest {
  id: string;
  business_id: string;
  resource_id: string;
  payroll_entry_id: string | null;
  request_type: 'change' | 'add' | 'add_flat_rate';
  
  // Original entry data
  original_clock_in?: string;
  original_clock_out?: string;
  original_break_start?: string;
  original_break_end?: string;
  original_total_minutes?: number;
  original_total_earnings?: number;
  original_notes?: string;
  
  // Requested changes
  requested_clock_in?: string;
  requested_clock_out?: string;
  requested_break_start?: string;
  requested_break_end?: string;
  requested_total_minutes?: number;
  requested_total_earnings?: number;
  requested_notes?: string;
  
  // For new entry requests
  requested_work_date?: string;
  requested_booking_id?: string;
  
  // For flat rate requests
  requested_flat_rate_item_id?: string;
  requested_flat_rate_hours?: number;
  requested_flat_rate_completed_at?: string;
  
  // Request metadata
  request_reason?: string;
  status: 'pending' | 'approved' | 'denied';
  requested_at: string;
  reviewed_at?: string;
  reviewed_by?: string;
  reviewer_notes?: string;
  
  created_at: string;
  updated_at: string;
  
  // Related data
  resource?: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
  };
  payroll_entry?: {
    id: string;
    clock_in?: string;
    clock_out?: string;
    total_minutes?: number;
    total_earnings?: number;
  };
  booking?: {
    id: string;
    booking_number: number;
    title: string;
  };
  flat_rate_item?: {
    id: string;
    name: string;
    description?: string;
  };
}

interface PayrollRequestsSectionProps {
  businessId: string;
  onRequestProcessed?: () => void;
  /** When provided (e.g. from ManagePayroll modal), filter is controlled by parent */
  activeFilter?: 'pending' | 'approved' | 'denied';
  onFilterChange?: (filter: 'pending' | 'approved' | 'denied') => void;
}

export default function PayrollRequestsSection({ businessId, onRequestProcessed, activeFilter: activeFilterProp, onFilterChange }: PayrollRequestsSectionProps) {
  const [requests, setRequests] = useState<PayrollRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [processingRequest, setProcessingRequest] = useState<string | null>(null);
  const [expandedRequests, setExpandedRequests] = useState<Set<string>>(new Set());
  const [internalFilter, setInternalFilter] = useState<'pending' | 'approved' | 'denied'>('pending');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const requestMinutes = requests.flatMap((request) => [
    request.requested_total_minutes || 0,
    request.original_total_minutes || 0,
    Math.abs((request.requested_total_minutes || 0) - (request.original_total_minutes || 0)),
  ]);
  const hoursText = usePayrollHoursText(requestMinutes);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Use controlled filter from parent when provided, otherwise internal state
  const activeFilter = activeFilterProp ?? internalFilter;
  const setActiveFilter = onFilterChange ?? setInternalFilter;

  useEffect(() => {
    loadRequests();
  }, [businessId, activeFilter]);

  // Handle click outside dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const loadRequests = async () => {
    if (!businessId) return;

    try {
      setLoading(true);
      const data = await getPayrollRequestsForBusiness(businessId, activeFilter);
      setRequests(data);
    } catch (error) {
      console.error('Error loading payroll requests:', error);
      toast.error('Failed to load payroll requests');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (request: PayrollRequest) => {
    try {
      setProcessingRequest(request.id);

      // Shared with the AI assistant — approves the request AND applies it
      // (flat-rate + payroll entry inserts, timeclock insert, or entry update).
      await approvePayrollRequest(request);

      const label =
        request.request_type === 'add_flat_rate' ? 'flat rate' : request.request_type === 'add' ? 'add entry' : 'change';
      toast.success(`Approved ${label} request from ${request.resource?.first_name} ${request.resource?.last_name}`);

      // Reload requests and notify parent
      await loadRequests();
      onRequestProcessed?.();
      
    } catch (error) {
      console.error('Error approving request:', error);
      toast.error('Failed to approve request');
    } finally {
      setProcessingRequest(null);
    }
  };

  const handleDeny = async (request: PayrollRequest, reason?: string) => {
    try {
      setProcessingRequest(request.id);
      
      await updatePayrollRequestStatus(request.id, 'denied', reason);
      
      toast.success(`Denied change request from ${request.resource?.first_name} ${request.resource?.last_name}`);
      
      // Reload requests
      await loadRequests();
      
    } catch (error) {
      console.error('Error denying request:', error);
      toast.error('Failed to deny change request');
    } finally {
      setProcessingRequest(null);
    }
  };

  const toggleExpanded = (requestId: string) => {
    setExpandedRequests(prev => {
      const next = new Set(prev);
      if (next.has(requestId)) {
        next.delete(requestId);
      } else {
        next.add(requestId);
      }
      return next;
    });
  };

  const getTimeDifference = (original: number, requested: number) => {
    const difference = requested - original;
    if (difference === 0) return null;
    
    const sign = difference > 0 ? '+' : '-';
    const label = hoursText(Math.abs(difference));
    if (!label) return null;

    return {
      text: `${sign}${label}`,
      isIncrease: difference > 0
    };
  };

  const getRequestTypeLabel = (requestType: string) => {
    switch (requestType) {
      case 'change': return 'Time Change';
      case 'add': return 'Add Entry';
      case 'add_flat_rate': return 'Add Flat Rate';
      default: return 'Request';
    }
  };

  const getRequestTypeBadge = (requestType: string) => {
    switch (requestType) {
      case 'change': return 'bg-blue-100 text-blue-700';
      case 'add': return 'bg-green-100 text-green-700';
      case 'add_flat_rate': return 'bg-purple-100 text-purple-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  // Since we now load only the filtered requests, use requests directly
  const filteredRequests = requests;
  
  // For count display, we show the current loaded count
  // Note: This only shows count for the currently loaded filter
  const currentCount = requests.length;

  if (loading) {
    return (
      <div className="p-4 sm:p-8">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
          <span className="ml-3 text-gray-600">Loading payroll requests...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Content */}
      <div className="flex-1 overflow-y-auto bg-gray-50 p-6">
        {filteredRequests.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No {activeFilter.charAt(0).toUpperCase() + activeFilter.slice(1)} Requests
            </h3>
            <p className="text-gray-500">
              {activeFilter === 'pending' 
                ? 'No pending payroll requests to review.'
                : activeFilter === 'approved'
                ? 'No approved payroll requests.'
                : 'No denied payroll requests.'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredRequests.map((request) => {
                const isExpanded = expandedRequests.has(request.id);
                const timeDiff = request.request_type === 'change' ? getTimeDifference(
                  request.original_total_minutes || 0,
                  request.requested_total_minutes || 0
                ) : null;

                return (
                  <div key={request.id} className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-all duration-200">
                        {/* Request Header */}
                        <div 
                          className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                          onClick={() => toggleExpanded(request.id)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="h-10 w-10 rounded-full bg-blue-500 flex items-center justify-center text-white font-semibold text-sm">
                                {request.resource?.first_name?.[0]}{request.resource?.last_name?.[0]}
                              </div>
                              <div>
                                <h4 className="font-medium text-gray-900 text-base">
                                  {request.resource?.first_name} {request.resource?.last_name}
                                </h4>
                                <p className="text-sm text-gray-600">
                                  {format(new Date(request.requested_at), 'MMM d, yyyy h:mm a')}
                                </p>
                              </div>
                            </div>
                            
                            <div className="flex items-center gap-2">
                              <span className={`px-3 py-1 rounded-md text-xs font-medium ${getRequestTypeBadge(request.request_type)}`}>
                                {getRequestTypeLabel(request.request_type)}
                              </span>
                              {timeDiff && (
                                <span className={`px-3 py-1 rounded-md text-xs font-medium ${
                                  timeDiff.isIncrease ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                                }`}>
                                  {timeDiff.text}
                                </span>
                              )}
                              <span className={`px-3 py-1 rounded-md text-xs font-semibold ${
                                request.status === 'pending' 
                                  ? 'bg-orange-100 text-orange-700'
                                  : request.status === 'approved'
                                  ? 'bg-green-100 text-green-700'
                                  : 'bg-red-100 text-red-700'
                              }`}>
                                {request.status.toUpperCase()}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Expanded Details */}
                        {isExpanded && (
                          <div className="border-t border-gray-200 p-4 bg-gray-50">
                            {request.request_type === 'add_flat_rate' ? (
                              /* Flat Rate Request Details */
                              <>
                                <div className="mb-4">
                                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                                    <h5 className="font-semibold text-gray-900 mb-3 flex items-center gap-2 text-sm">
                                      <Package className="h-4 w-4 text-purple-600" />
                                      Requested Flat Rate Item
                                    </h5>
                                    <div className="space-y-2 text-sm">
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">Job:</span>
                                        <span className="font-medium">
                                          {request.booking ? `#${request.booking.booking_number} - ${request.booking.title}` : request.requested_booking_id || '-'}
                                        </span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">Flat Rate Item:</span>
                                        <span className="font-medium">
                                          {request.flat_rate_item ? request.flat_rate_item.name : request.requested_flat_rate_item_id || '-'}
                                        </span>
                                      </div>
                                      {request.flat_rate_item?.description && (
                                        <div className="flex justify-between">
                                          <span className="text-gray-600">Description:</span>
                                          <span className="font-medium text-gray-700">{request.flat_rate_item.description}</span>
                                        </div>
                                      )}
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">Hours:</span>
                                        <span className="font-medium">{request.requested_flat_rate_hours || 0} hours</span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">Will be marked completed on:</span>
                                        <span className="font-medium text-purple-700">
                                          {request.requested_flat_rate_completed_at 
                                            ? (
                                              <div className="text-right">
                                                <div>{format(new Date(request.requested_flat_rate_completed_at), 'MMM d, yyyy h:mm a')}</div>
                                                <div className="text-xs text-purple-600 mt-1">
                                                  (Inherited from existing group)
                                                </div>
                                              </div>
                                            )
                                            : (
                                              <div className="text-right">
                                                <div>Current date/time when approved</div>
                                                <div className="text-xs text-orange-600 mt-1">
                                                  (No existing group to inherit from)
                                                </div>
                                              </div>
                                            )
                                          }
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </>
                            ) : request.request_type === 'add' ? (
                              /* Add Entry Request Details */
                              <>
                                <div className="mb-4">
                                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                                    <h5 className="font-semibold text-gray-900 mb-3 flex items-center gap-2 text-sm">
                                      <Calendar className="h-4 w-4 text-green-600" />
                                      Requested New Entry
                                    </h5>
                                    <div className="space-y-2 text-sm">
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">Work Date:</span>
                                        <span className="font-medium">
                                          {request.requested_work_date ? (() => {
                                            const [year, month, day] = request.requested_work_date.split('-').map(Number);
                                            return format(new Date(year, month - 1, day), 'MMM d, yyyy');
                                          })() : '-'}
                                        </span>
                                      </div>
                                      {request.booking && (
                                        <div className="flex justify-between">
                                          <span className="text-gray-600">Job:</span>
                                          <span className="font-medium">
                                            #{request.booking.booking_number} - {request.booking.title}
                                          </span>
                                        </div>
                                      )}
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">Clock In:</span>
                                        <span className="font-medium">
                                          {request.requested_clock_in ? format(parseISO(request.requested_clock_in), 'MMM d, yyyy h:mm a') : '-'}
                                        </span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">Clock Out:</span>
                                        <span className="font-medium">
                                          {request.requested_clock_out ? format(parseISO(request.requested_clock_out), 'MMM d, yyyy h:mm a') : '-'}
                                        </span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">Break Start:</span>
                                        <span className="font-medium">
                                          {request.requested_break_start ? format(parseISO(request.requested_break_start), 'MMM d, yyyy h:mm a') : '-'}
                                        </span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">Break End:</span>
                                        <span className="font-medium">
                                          {request.requested_break_end ? format(parseISO(request.requested_break_end), 'MMM d, yyyy h:mm a') : '-'}
                                        </span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">Total Time:</span>
                                        <span className="font-medium">{hoursText(request.requested_total_minutes || 0)}</span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">Estimated Earnings:</span>
                                        <span className="font-medium">${(request.requested_total_earnings || 0).toFixed(2)}</span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </>
                            ) : (
                              /* Change Request Details */
                              <>
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                                  {/* Original Entry */}
                                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                                    <h5 className="font-semibold text-gray-900 mb-3 flex items-center gap-2 text-sm">
                                      <Clock className="h-4 w-4 text-gray-600" />
                                      Original Entry
                                    </h5>
                                    <div className="space-y-2 text-sm">
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">Clock In:</span>
                                        <span className="font-medium">
                                          {request.original_clock_in ? format(parseISO(request.original_clock_in), 'MMM d, yyyy h:mm a') : '-'}
                                        </span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">Clock Out:</span>
                                        <span className="font-medium">
                                          {request.original_clock_out ? format(parseISO(request.original_clock_out), 'MMM d, yyyy h:mm a') : '-'}
                                        </span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">Break Start:</span>
                                        <span className="font-medium">
                                          {request.original_break_start ? format(parseISO(request.original_break_start), 'MMM d, yyyy h:mm a') : '-'}
                                        </span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">Break End:</span>
                                        <span className="font-medium">
                                          {request.original_break_end ? format(parseISO(request.original_break_end), 'MMM d, yyyy h:mm a') : '-'}
                                        </span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">Total Time:</span>
                                        <span className="font-medium">{hoursText(request.original_total_minutes || 0)}</span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">Earnings:</span>
                                        <span className="font-medium">${(request.original_total_earnings || 0).toFixed(2)}</span>
                                      </div>
                                    </div>
                                  </div>

                                  {/* Requested Changes */}
                                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                                    <h5 className="font-semibold text-gray-900 mb-3 flex items-center gap-2 text-sm">
                                      <Calendar className="h-4 w-4 text-blue-600" />
                                      Requested Changes
                                    </h5>
                                    <div className="space-y-2 text-sm">
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">Clock In:</span>
                                        <span className="font-medium">
                                          {request.requested_clock_in ? format(parseISO(request.requested_clock_in), 'MMM d, yyyy h:mm a') : '-'}
                                        </span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">Clock Out:</span>
                                        <span className="font-medium">
                                          {request.requested_clock_out ? format(parseISO(request.requested_clock_out), 'MMM d, yyyy h:mm a') : '-'}
                                        </span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">Break Start:</span>
                                        <span className="font-medium">
                                          {request.requested_break_start ? format(parseISO(request.requested_break_start), 'MMM d, yyyy h:mm a') : '-'}
                                        </span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">Break End:</span>
                                        <span className="font-medium">
                                          {request.requested_break_end ? format(parseISO(request.requested_break_end), 'MMM d, yyyy h:mm a') : '-'}
                                        </span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">Total Time:</span>
                                        <span className="font-medium">{hoursText(request.requested_total_minutes || 0)}</span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">Earnings:</span>
                                        <span className="font-medium">${(request.requested_total_earnings || 0).toFixed(2)}</span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </>
                            )}

                            {/* Request Reason */}
                            {request.request_reason && (
                              <div className="mb-4">
                                <h5 className="font-semibold text-gray-900 mb-2 text-sm">
                                  {request.request_type === 'add_flat_rate' ? 'Reason for Additional Items:' : 
                                   request.request_type === 'add' ? 'Reason for Missing Entry:' : 
                                   'Reason for Change:'}
                                </h5>
                                <p className="text-gray-700 bg-white rounded-lg p-3 italic border border-gray-200 text-sm">
                                  "{request.request_reason}"
                                </p>
                              </div>
                            )}

                            {/* Action Buttons (only for pending requests) */}
                            {request.status === 'pending' ? (
                              <div className="flex flex-col sm:flex-row gap-3">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleApprove(request);
                                  }}
                                  disabled={processingRequest === request.id}
                                  className="flex items-center justify-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm"
                                >
                                  {processingRequest === request.id ? (
                                    <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                  ) : (
                                    <CheckCircle className="h-4 w-4" />
                                  )}
                                  {request.request_type === 'add_flat_rate' ? 'Approve Flat Rate' : 
                                   request.request_type === 'add' ? 'Approve Entry' : 
                                   'Approve Changes'}
                                </button>
                                
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeny(request);
                                  }}
                                  disabled={processingRequest === request.id}
                                  className="flex items-center justify-center gap-2 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm"
                                >
                                  <XCircle className="h-4 w-4" />
                                  Deny Request
                                </button>
                              </div>
                            ) : (
                              /* Review Information for processed requests */
                              <div className="bg-white rounded-lg p-4 border border-gray-200">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-3">
                                    {request.status === 'approved' ? (
                                      <CheckCircle className="h-5 w-5 text-green-600" />
                                    ) : (
                                      <XCircle className="h-5 w-5 text-red-600" />
                                    )}
                                    <div>
                                      <h6 className="font-semibold text-gray-900 text-sm">
                                        {request.status === 'approved' ? 'Request Approved' : 'Request Denied'}
                                      </h6>
                                      <p className="text-sm text-gray-600">
                                        {request.reviewed_at 
                                          ? `Reviewed on ${format(new Date(request.reviewed_at), 'MMM d, yyyy h:mm a')}`
                                          : `Processed on ${format(new Date(request.requested_at), 'MMM d, yyyy h:mm a')}`
                                        }
                                      </p>
                                    </div>
                                  </div>
                                </div>
                                {request.reviewer_notes && (
                                  <div className="mt-3 pt-3 border-t border-gray-200">
                                    <p className="text-sm text-gray-700">
                                      <span className="font-medium">Reviewer Note:</span> "{request.reviewer_notes}"
                                    </p>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
        )}
      </div>
    </div>
  );
}